import React, { useState } from 'react';
import { Grade, Subject, Tenant, UserProfile } from '../types';
import { Dropdown } from './Dropdown';
import { FileUpload } from './FileUpload';
import { ATYPICAL_GRADES, SUBJECTS } from '../constants';
import { runService } from '../services/runService';
import { generateATypicalPdf } from '../utils/aTypicalPdfGenerator';
import { useAuth } from '../contexts/AuthContext';

interface ATypicalModuleProps {
  tenant: Tenant;
  userProfile: UserProfile;
  brandLogo: string;
  partnerLogo: string;
}

export interface AnalysisInputSummary {
  studentName: string;
  grade: Grade;
  subject: Subject;
  source: string;
}

export const ATypicalModule: React.FC<ATypicalModuleProps> = ({ tenant, userProfile, brandLogo, partnerLogo }) => {
  const [showInputForm, setShowInputForm] = useState<boolean>(false);
  const [studentName, setStudentName] = useState<string>('');
  const [grade, setGrade] = useState<Grade>(Grade.GRADE_6);
  const [subject, setSubject] = useState<Subject>(Subject.ENGLISH);
  const [handwritingFiles, setHandwritingFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticResultText, setDiagnosticResultText] = useState<string | null>(null);
  const [analysisInputs, setAnalysisInputs] = useState<AnalysisInputSummary | null>(null);


  const { updateTenant } = useAuth();

  const handleAnalyzeHandwriting = async () => {
    if (!studentName.trim()) {
      setError("Please enter the student's name.");
      return;
    }
    if (handwritingFiles.length === 0) {
      setError("Please choose one or more files to analyze.");
      return;
    }
    setError(null);
    setLoading(true);
    setDiagnosticResultText(null);


    try {
      const result = await runService.startRun(tenant, userProfile, 'atypical', {
        grade,
        subject,
        files: handwritingFiles,
      });

const { resultText } = result.aiResult;

      
      const sourceName = `${handwritingFiles.length} file(s) analyzed`;
      setAnalysisInputs({ studentName, grade, subject, source: sourceName });
      setDiagnosticResultText(resultText);
     
      
      if (result?.newTenant) {
  updateTenant(result.newTenant); // Update global tenant state
}

      
      setShowInputForm(false); // Hide form to show results view

    } catch (err: any) {
      console.error("Handwriting analysis failed:", err);
      setError(err.message || "Failed to analyze handwriting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetFormState = () => {
    setStudentName('');
    setGrade(Grade.GRADE_6);
    setSubject(Subject.ENGLISH);
    setHandwritingFiles([]);
    setLoading(false);
    setError(null);
    setDiagnosticResultText(null);
    setAnalysisInputs(null);
 
  };

  const handleDownloadPdf = () => {
    if (diagnosticResultText && analysisInputs) {
    generateATypicalPdf(diagnosticResultText, analysisInputs, brandLogo, partnerLogo, tenant?.tenantLogoUrl);
    } else {
      console.error("Cannot generate PDF: Missing result text or analysis inputs.");
    }
  };

  const renderInitialView = () => (
    <div
      onClick={() => {
        resetFormState();
        setShowInputForm(true);
      }}
      className="flex-grow flex flex-col justify-center items-center text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-4 rounded-b-xl"
      role="button"
      aria-label="Open Student Work Diagnostics Module"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-brand-orange mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
      </svg>
      <p className="text-brand-grey text-lg mb-6 max-w-xs">
        Extracts thinking signals from student answers.
      </p>
      <p className="text-brand-orange font-semibold mt-auto pt-4">
        Begin analysis →
      </p>
    </div>
  );

  const renderInputForm = () => (
    <>
      <button
        onClick={() => {
          resetFormState();
          setShowInputForm(false);
        }}
        className="mb-6 px-4 py-2 bg-gray-200 text-brand-grey text-sm font-semibold rounded-md shadow-sm hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
        aria-label="Back to module selection"
      >
        ← Back
      </button>

      <div className="mb-4">
        <label htmlFor="student-name" className="block text-base font-bold text-gray-600 mb-2">
          Student Name
        </label>
        <input
          type="text"
          id="student-name"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          placeholder="e.g., Jane Doe"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-orange focus:border-brand-orange sm:text-sm text-brand-grey bg-white"
          required
        />
      </div>

      <Dropdown
        label="Grade"
        options={ATYPICAL_GRADES}
        selectedValue={grade}
        onValueChange={(value) => setGrade(value as Grade)}
        id="atypical-grade-select"
      />

      <Dropdown
        label="Subject"
        options={SUBJECTS}
        selectedValue={subject}
        onValueChange={(value) => setSubject(value as Subject)}
        id="atypical-subject-select"
      />

      <FileUpload
        label="Student Work (Images or PDFs)"
        accept="image/*,.pdf"
        onFileChange={setHandwritingFiles}
        errorMessage={error}
        multiple={true}
      />

      <button
        onClick={handleAnalyzeHandwriting}
        disabled={loading}
        className={`w-full px-6 py-3 bg-brand-orange text-white text-lg font-semibold rounded-md shadow-md hover:bg-opacity-90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-opacity-50
          ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        aria-label="Extract Signals"
      >
        {loading ? (
          <svg className="animate-spin h-5 w-5 text-white inline-block mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          'Extract Signals'
        )}
      </button>
    </>
  );

  const renderResultsView = () => (
    <>
      <div className="mb-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-start">
            <div>
                <h3 className="text-xl font-semibold text-brand-grey mb-4">Input Summary</h3>
                <ul className="text-brand-grey space-y-2">
                <li><strong>Student Name:</strong> {analysisInputs?.studentName}</li>
                <li><strong>Grade:</strong> {analysisInputs?.grade}</li>
                <li><strong>Subject:</strong> {analysisInputs?.subject}</li>
                <li className="break-words"><strong>Source:</strong> {analysisInputs?.source}</li>
                </ul>
            </div>
            <button
                onClick={handleDownloadPdf}
                className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90 transition-colors duration-200 flex items-center space-x-2"
                aria-label="Download report as PDF"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download PDF</span>
            </button>
        </div>
      </div>


      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-brand-grey">
        <h3 className="text-2xl font-semibold text-brand-grey mb-4">Diagnostic Analysis</h3>
        <div className="whitespace-pre-wrap break-words text-base bg-white p-4 rounded-md border border-gray-300 max-h-96 overflow-y-auto">
          {diagnosticResultText}
        </div>
        
      </div>
      <button
        onClick={() => {
          resetFormState();
          setShowInputForm(true);
        }}
        className="mt-8 w-full px-6 py-3 bg-brand-orange text-white text-lg font-semibold rounded-md shadow-md hover:bg-opacity-90 transition-colors duration-200"
        aria-label="Run a new analysis"
      >
        Run New Analysis
      </button>
    </>
  );

  return (
    <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-8 md:p-10 lg:p-12 flex flex-col h-full transition-all duration-300 ease-in-out hover:shadow-2xl hover:-translate-y-2">
      <h2 className="text-3xl font-extrabold text-brand-grey mb-8 border-b-2 border-brand-orange pb-2">
        A-<span className="text-brand-orange">Typical</span><sup className="text-sm">™</sup>: Student Work <span className="text-brand-orange">Diagnostics</span>
      </h2>
      <div className="flex-grow flex flex-col">
        {diagnosticResultText && analysisInputs
          ? renderResultsView()
          : showInputForm
          ? renderInputForm()
          : renderInitialView()}
      </div>
    </div>
  );
};